import React, { useState, useEffect } from "react";
import { View, StyleSheet, TextInput, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { UserReviewType } from "@/types/db/user_reviews";
import { Button } from "@/components/ui/Button";
import { Menu } from "@/components/Menu";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import Avatar from "@/components/Avatar";
import { UserInput } from "@/components/inputs/UserInput";
import { supabase } from "@/lib/supabase-client";

export interface ReviewCardProps {
  review: UserReviewType;
  canUpdate: boolean;
  canDelete: boolean;
  onSave: (review: UserReviewType) => Promise<void>;
  onRemove: (reviewId: string) => Promise<void>;
}

export function ReviewCard({ review, canUpdate, canDelete, onSave, onRemove }: ReviewCardProps) {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { user } = useAuth();
  const isNewReview = !review.id || review.id.startsWith("new-");

  const [updatedByName, setUpdatedByName] = useState<string | null>(null);

  useEffect(() => {
    if (!review.updated_by) return;
    supabase
      .from("profiles")
      .select("preferred_name, first_name, last_name")
      .eq("id", review.updated_by)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setUpdatedByName(data.preferred_name || [data.first_name, data.last_name].filter(Boolean).join(" ") || null);
        }
      });
  }, [review.updated_by]);

  // Local edit state - new reviews start in edit mode
  const [isEditing, setIsEditing] = useState(isNewReview);
  const [editedReview, setEditedReview] = useState<UserReviewType>(() => ({
    ...review,
    submitted_by: review.submitted_by || user?.id,
  }));

  // Reset edited review when review prop changes
  useEffect(() => {
    setEditedReview(review);
  }, [review]);

  // When a new review is added, enter edit mode
  useEffect(() => {
    if (isNewReview) {
      setIsEditing(true);
    }
  }, [isNewReview]);

  const handleSaveAndClose = async () => {
    await onSave(editedReview);
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (isNewReview) {
      onRemove(review.id);
    } else {
      setEditedReview(review);
    }
    setIsEditing(false);
  };

  const handleRatingChange = (newRating: number) => {
    setEditedReview((prev) => ({ ...prev, rating: newRating }));
  };

  const renderStars = (rating: number, interactive = false) => {
    return (
      <View style={styles.starsRow}>
        {[1, 2, 3, 4, 5].map((star) =>
          interactive ? (
            <Pressable key={star} onPress={() => handleRatingChange(star)}>
              <MaterialCommunityIcons
                name={star <= rating ? "star" : "star-outline"}
                size={24}
                color={star <= rating ? colors.amber.text : colors.textSecondary}
              />
            </Pressable>
          ) : (
            <MaterialCommunityIcons
              key={star}
              name={star <= rating ? "star" : "star-outline"}
              size={16}
              color={star <= rating ? colors.amber.text : colors.textSecondary}
            />
          ),
        )}
      </View>
    );
  };

  // View mode
  if (!isEditing) {
    return (
      <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.reviewHeader}>
          <View style={styles.reviewHeaderLeft}>
            {renderStars(review.rating)}
            {review.meta_data?.category && (
              <View
                style={[
                  styles.categoryBadge,
                  { backgroundColor: colors.indigo.background, borderColor: colors.indigo.border },
                ]}
              >
                <ThemedText style={[styles.categoryText, { color: colors.indigo.text }]}>
                  {review.meta_data.category}
                </ThemedText>
              </View>
            )}
          </View>
          <View style={styles.headerRight}>
            <ThemedText style={[styles.dateText, { color: colors.textSecondary }]}>
              {formatDate(review.created_at)}
            </ThemedText>
            {(canUpdate || canDelete) && (
              <Menu
                buttonStyle={{ borderWidth: 0, backgroundColor: "transparent", minHeight: 28 }}
                hideArchive={true}
                hideDuplicate={true}
                onEdit={canUpdate ? () => setIsEditing(true) : undefined}
                onDelete={canDelete ? () => onRemove(review.id) : undefined}
              />
            )}
          </View>
        </View>

        {review.message && <ThemedText style={styles.messageText}>{review.message}</ThemedText>}

        {review.submitter && (
          <View style={styles.reviewerRow}>
            <Avatar userId={review.submitted_by} size={20} />
            <ThemedText style={[styles.reviewerText, { color: colors.text }]}>
              {review.submitter.first_name} {review.submitter.last_name}
            </ThemedText>
          </View>
        )}

        <View style={styles.auditRow}>
          <View style={styles.auditItem}>
            <ThemedText style={[styles.auditLabel, { color: colors.textSecondary }]}>Created</ThemedText>
            <ThemedText style={[styles.auditValue, { color: colors.textSecondary }]}>
              {formatDate(review.created_at)}
            </ThemedText>
            {review.created_by && (
              <View style={styles.auditUser}>
                <Avatar userId={review.created_by} size={16} fontSize={8} />
                {review.created_by_user && (
                  <ThemedText style={[styles.auditUserName, { color: colors.textSecondary }]}>
                    {review.created_by_user.first_name} {review.created_by_user.last_name}
                  </ThemedText>
                )}
              </View>
            )}
          </View>
          {review.updated_at && (
            <View style={styles.auditItem}>
              <ThemedText style={[styles.auditLabel, { color: colors.textSecondary }]}>Updated</ThemedText>
              <ThemedText style={[styles.auditValue, { color: colors.textSecondary }]}>
                {formatDate(review.updated_at)}
              </ThemedText>
              {review.updated_by && (
                <View style={styles.auditUser}>
                  <Avatar userId={review.updated_by} size={16} fontSize={8} />
                  {updatedByName && (
                    <ThemedText style={[styles.auditUserName, { color: colors.textSecondary }]}>
                      {updatedByName}
                    </ThemedText>
                  )}
                </View>
              )}
            </View>
          )}
        </View>
      </View>
    );
  }

  // Edit mode
  return (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.editForm}>
        {/* Header row with rating + save/cancel buttons */}
        <View style={styles.editHeader}>
          <View style={styles.ratingRow}>
            <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Rating:</ThemedText>
            {renderStars(editedReview.rating, true)}
          </View>
          <View style={styles.headerActions}>
            <Button icon="close" style={[styles.iconBtn, { borderColor: colors.border }]} onPress={handleCancel} />
            <Button
              icon="content-save"
              style={[styles.iconBtn, styles.saveIconBtn, { backgroundColor: colors.primary }]}
              textStyle={{ color: "white" }}
              onPress={handleSaveAndClose}
            />
          </View>
        </View>

        {/* Title */}
        <TextInput
          style={[
            styles.input,
            {
              borderColor: colors.border,
              color: colors.text,
              backgroundColor: colors.backgroundSecondary,
              width: "100%",
            },
          ]}
          placeholder="Title (e.g. Quarterly Review, Q4 2024)"
          placeholderTextColor={colors.placeholder}
          value={editedReview.meta_data?.category || ""}
          onChangeText={(value) =>
            setEditedReview((prev) => ({ ...prev, meta_data: { ...prev.meta_data, category: value } }))
          }
        />

        {/* Message */}
        <View style={styles.messageSection}>
          <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Review:</ThemedText>
          <TextInput
            style={[
              styles.input,
              styles.textArea,
              { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
            ]}
            placeholder="Write your review..."
            placeholderTextColor={colors.placeholder}
            value={editedReview.message || ""}
            onChangeText={(value) => setEditedReview((prev) => ({ ...prev, message: value }))}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
        </View>

        {/* Submitted By */}
        <View style={styles.row}>
          <ThemedText style={[styles.label, { color: colors.textSecondary }]}>Submitted By:</ThemedText>
          <View style={styles.flexInput}>
            <UserInput
              value={editedReview.submitted_by || null}
              state="editing"
              label=""
              placeholder="Select reviewer\u2026"
              showEmail={false}
              onChange={(userId) => setEditedReview((prev) => ({ ...prev, submitted_by: userId || undefined }))}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    padding: 16,
    gap: 12,
  },
  reviewHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  reviewHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  starsRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
    borderWidth: 1,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: "600",
  },
  dateText: {
    fontSize: 12,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
  },
  reviewerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  reviewerText: {
    fontSize: 13,
    fontWeight: "500",
  },
  editForm: {
    gap: 12,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  label: {
    fontSize: 14,
    minWidth: 70,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  flexInput: {
    flex: 1,
  },
  messageSection: {
    gap: 8,
  },
  textArea: {
    height: 100,
    paddingTop: 10,
    paddingBottom: 10,
  },
  editHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  iconBtn: {
    height: 40,
    width: 40,
    paddingHorizontal: 0,
    borderRadius: 8,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  saveIconBtn: {
    borderWidth: 0,
  },
  auditRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 24,
  },
  auditItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  auditLabel: {
    fontSize: 10,
    textTransform: "uppercase",
  },
  auditValue: {
    fontSize: 11,
  },
  auditUser: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  auditUserName: {
    fontSize: 10,
  },
});
